import React, { useEffect } from "react";
import { ModalChecklistACVProps } from "./Types";
import { Button, ButtonGroup, Card, Col, Form, Row, ToggleButton } from "react-bootstrap";
import blogFetch from "../../axios/config";
import toast from "react-hot-toast";

const ModalChecklistACV: React.FC<ModalChecklistACVProps> = ({
    checklistOpen,
    setChecklistOpen,
    ciclo_isaga,
    onChecklistSaved
}) => {

    const getCurrentTime = () => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const [checklistData, setChecklistData] = React.useState({
        ciclo_isaga: ciclo_isaga,
        horario: getCurrentTime(),
        pressaoOK: "",
        pressaoSP: 0.0,
        pressaoPV: 0.0,
        vacuoOK: "",
        vacuoPV: 0.0,
        TemperaturaOK: "",
        temperaturaSP: 0.0,
        temperaturaPV: 0.0,
        graficoOK: "",
        observacoes: "",
        operador: "",
    });

    const handleChange = (name: string, value: string) => {
        setChecklistData((prev) => ({ ...prev, [name]: value }));
    };

    const clearForm = () => {
        setChecklistData({
            ciclo_isaga: "",
            horario: "",
            pressaoOK: "",
            pressaoSP: 0.0,
            pressaoPV: 0.0,
            vacuoOK: "",
            vacuoPV: 0.0,
            TemperaturaOK: "",
            temperaturaSP: 0.0,
            temperaturaPV: 0.0,
            graficoOK: "",
            observacoes: "",
            operador: "",
        });
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setChecklistOpen(false);
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [setChecklistOpen]);

    if (!checklistOpen) return null;

    const submitChecklist = async () => {
        try {
            const body = {
                ciclo: checklistData.ciclo_isaga,
                horario: checklistData.horario,
                pressaoOK: checklistData.pressaoOK === "ok",
                pressaoSP: checklistData.pressaoSP,
                pressaoPV: checklistData.pressaoPV,
                vacuoOK: checklistData.vacuoOK === "ok",
                vacuoPV: checklistData.vacuoPV,
                temperaturaOK: checklistData.TemperaturaOK === "ok",
                temperaturaSP: checklistData.temperaturaSP,
                temperaturaPV: checklistData.temperaturaPV,
                graficoOK: checklistData.graficoOK === "ok",
                observacoes: checklistData.observacoes || "",
                operador: checklistData.operador || ""
            };

            await blogFetch.post("iSAGA_ACV_Acompanhamento/checklist-acv", body);

            toast.success("Checklist enviado com sucesso!");
            
            setChecklistOpen(false);

            if (onChecklistSaved) onChecklistSaved();
            clearForm();

        } catch (error) {
            console.error("Erro ao enviar checklist:", error);
            toast.error("Erro ao enviar checklist.");
        }
    };

    const renderOkNok = (name: string) => (
        <ButtonGroup className="d-flex justify-between gap-2 mt-1">
            <ToggleButton
                id={`${name}-ok`}
                type="radio"
                variant={checklistData[name as keyof typeof checklistData] === "ok" ? "success" : "outline-success"}
                name={name}
                value="ok"
                onChange={(e) => handleChange(name, e.currentTarget.value)}
                size="sm"
            >
                Ok
            </ToggleButton>
            <ToggleButton
                id={`${name}-nok`}
                type="radio"
                variant={checklistData[name as keyof typeof checklistData] === "nok" ? "danger" : "outline-danger"}
                name={name}
                value="nok"
                onChange={(e) => handleChange(name, e.currentTarget.value)}
                size="sm"
            >
                Ñ Ok
            </ToggleButton>
        </ButtonGroup>
    );

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50 backdrop-blur"
            onClick={() => setChecklistOpen(false)}
        >
            <div
                className="bg-white rounded-4 shadow-lg p-4 w-75 overflow-auto"
                style={{ maxHeight: "90vh" }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Cabeçalho */}
                <div className="d-flex justify-content-between align-items-center border-bottom pb-3 sticky-top bg-white" style={{ top: 0, zIndex: 10 }}>
                    <h4 className="fw-bold text-primary mb-0">Checklist de Autoclave</h4>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="rounded-circle px-2 py-1 fw-bold"
                        onClick={() => setChecklistOpen(false)}
                    >
                        ✕
                    </Button>
                </div>

                {/* Campos */}
                <Row xs={1} md={2} lg={3} xl={4} className="g-4 mt-2">
                    {/* Horário */}
                    <Col>
                        <Card className="shadow-sm border-0 rounded-3 h-100">
                            <Card.Body>
                                <Card.Title className="text-uppercase fs-6 fw-semibold text-secondary mb-2">
                                    Horário
                                </Card.Title>
                                <Form.Control
                                    type="time"
                                    className="text-center fw-medium"
                                    value={checklistData.horario}
                                    onChange={(e) =>
                                        setChecklistData((p) => ({ ...p, horario: e.target.value }))
                                    }
                                />
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Pressão */}
                    <Col>
                        <Card className="shadow-sm border-0 rounded-3 h-100">
                            <Card.Body>
                                <Card.Title className="text-uppercase fs-6 fw-semibold text-secondary mb-2">
                                    Pressão
                                </Card.Title>
                                {renderOkNok("pressaoOK")}
                                <Form.Group className="d-flex gap-2 mt-3 align-items-center">
                                    <Form.Label className="fw-semibold mb-0">SP</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        className="text-center"
                                        onChange={(e) =>
                                            setChecklistData((p) => ({
                                                ...p,
                                                pressaoSP: parseFloat(e.target.value),
                                            }))
                                        }
                                    />
                                    <Form.Label className="fw-semibold mb-0">PV</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        className="text-center"
                                        onChange={(e) =>
                                            setChecklistData((p) => ({
                                                ...p,
                                                pressaoPV: parseFloat(e.target.value),
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Vácuo */}
                    <Col>
                        <Card className="shadow-sm border-0 rounded-3 h-100">
                            <Card.Body>
                                <Card.Title className="text-uppercase fs-6 fw-semibold text-secondary mb-2">
                                    Vácuo
                                </Card.Title>
                                {renderOkNok("vacuoOK")}
                                <div className="d-flex gap-2 mt-3 align-items-center">
                                    <Form.Label className="fw-semibold mb-0">PV</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        className="text-center"
                                        onChange={(e) =>
                                            setChecklistData((p) => ({
                                                ...p,
                                                vacuoPV: parseFloat(e.target.value),
                                            }))
                                        }
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Temperatura */}
                    <Col>
                        <Card className="shadow-sm border-0 rounded-3 h-100">
                            <Card.Body>
                                <Card.Title className="text-uppercase fs-6 fw-semibold text-secondary mb-2">
                                    Temperatura
                                </Card.Title>
                                {renderOkNok("TemperaturaOK")}
                                <div className="d-flex gap-2 mt-3 align-items-center">
                                    <Form.Label className="fw-semibold mb-0">SP</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        className="text-center"
                                        onChange={(e) =>
                                            setChecklistData((p) => ({
                                                ...p,
                                                temperaturaSP: parseFloat(e.target.value),
                                            }))
                                        }
                                    />
                                    <Form.Label className="fw-semibold mb-0">PV</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        className="text-center"
                                        onChange={(e) =>
                                            setChecklistData((p) => ({
                                                ...p,
                                                temperaturaPV: parseFloat(e.target.value),
                                            }))
                                        }
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Gráfico */}
                    <Col>
                        <Card className="shadow-sm border-0 rounded-3 h-100">
                            <Card.Body>
                                <Card.Title className="text-uppercase fs-6 fw-semibold text-secondary mb-2">
                                    Gráfico (Proveta)
                                </Card.Title>
                                {renderOkNok("graficoOK")}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Observações */}
                    <Col md={12}>
                        <Card className="shadow-sm border-0 rounded-3 h-100">
                            <Card.Body>
                                <Card.Title className="text-uppercase fs-6 fw-semibold text-secondary mb-2">
                                    Problemas / Observações
                                </Card.Title>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Descreva se houver..."
                                    onChange={(e) =>
                                        setChecklistData((p) => ({
                                            ...p,
                                            observacoes: e.target.value,
                                        }))
                                    }
                                />
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Operador */}
                    <Col>
                        <Card className="shadow-sm border-0 rounded-3 h-100">
                            <Card.Body>
                                <Card.Title className="text-uppercase fs-6 fw-semibold text-secondary mb-2">
                                    Operador
                                </Card.Title>
                                <Form.Control
                                    type="text"
                                    onChange={(e) =>
                                        setChecklistData((p) => ({
                                            ...p,
                                            operador: e.target.value,
                                        }))
                                    }
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Rodapé */}
                <div className="d-flex justify-content-end gap-3 mt-4 border-top pt-3">
                    <Button
                        variant="outline-secondary"
                        onClick={() => {
                            clearForm();
                            setChecklistOpen(false);
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={submitChecklist}>
                        Salvar Checklist
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ModalChecklistACV;
